import { useState, useRef, useEffect } from 'react';

import { BackrsUser, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { usePostProfileOptionAnswerMutation } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { setUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Box, Button, Typography, SxProps, Theme } from '@mui/material';
import Cropper, { ReactCropperElement } from 'react-cropper';

import { uploadImage, isValidImageType } from './uploadImage';

import 'cropperjs/dist/cropper.css';

const BUTTON_STRINGS = appStrings.common.button;
const IMAGE_UPLOAD_STRINGS = appStrings.modules.profile.edit.imageUpload;

type ImageUploadDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  type: 'profile_media_id' | 'profile_banner_media_id';
  preLoadedImage?: string | null;
}>;

export const ImageUploadDialog = ({
  open,
  onClose,
  type,
  preLoadedImage,
}: ImageUploadDialogProps) => {
  const { user } = useAppSelector((state) => state.user);
  const [postProfileOptionAnswer] = usePostProfileOptionAnswerMutation();
  const dispatch = useAppDispatch();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<ReactCropperElement>(null);

  const isProfileImage = type === 'profile_media_id';

  // Handle preloaded image when dialog opens
  useEffect(() => {
    if (open && preLoadedImage) {
      setImageSrc(preLoadedImage);
    }
  }, [open, preLoadedImage]);

  const handleUpdateUserProfileImage = (profile_image: string) => {
    if (!user) return;

    const updatedUser: BackrsUser = {
      ...user,
      profile_image,
    };

    dispatch(setUser(updatedUser));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    setError(null);

    if (file && isValidImageType(file)) {
      setSelectedFile(file);
      const reader = new FileReader();

      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please select a PNG or JPG image file');
    }
  };

  const handleUpload = async () => {
    if (!user || !imageSrc || !cropperRef.current) return;

    try {
      setIsUploading(true);
      setError(null);

      const cropper = cropperRef.current.cropper;

      const canvas = cropper.getCroppedCanvas({
        maxWidth: isProfileImage ? 512 : 4800,
        maxHeight: isProfileImage ? 512 : 1600,
        imageSmoothingQuality: 'high',
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob: Blob | null) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas is empty'));
        }, 'image/jpeg');
      });

      const croppedFile = new File([blob], selectedFile?.name || 'avatar.jpg', {
        type: blob.type,
      });

      const token = getBackrsToken();

      if (!token) throw new Error('No auth token found');

      const { newDocumentUri, mediaId } = await uploadImage({
        file: croppedFile,
        token,
        userId: user.id,
      });

      const profileOptionAnswers = [
        {
          user_id: user.id,
          question_id: type,
          category_id: 'identity',
          value: mediaId,
        },
      ];

      await postProfileOptionAnswer({
        profileOptionAnswers,
        access_token: token,
        postKey: type,
      }).unwrap();

      if (isProfileImage) {
        handleUpdateUserProfileImage(newDocumentUri);
      }

      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error uploading image';

      setError(message);
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setImageSrc(null);
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedFile(null);
    setImageSrc(null);
    setError(null);
  };

  return (
    <InfoModal
      key={type}
      icon="Camera01"
      headerContent={
        <Box sx={headerContainerSx}>
          <Typography variant="text-lg-semibold">
            {isProfileImage
              ? IMAGE_UPLOAD_STRINGS.headshot.title
              : IMAGE_UPLOAD_STRINGS.banner.title}
          </Typography>
          <Typography variant="text-sm-regular">
            {isProfileImage
              ? IMAGE_UPLOAD_STRINGS.headshot.description
              : IMAGE_UPLOAD_STRINGS.banner.description}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodyContainerSx}>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileSelect}
          />

          {imageSrc && (
            <Box sx={cropperWrapperSx(isProfileImage)}>
              <Cropper
                src={imageSrc}
                style={cropperStyle}
                aspectRatio={isProfileImage ? 1 : 4}
                autoCropArea={1}
                viewMode={1}
                checkOrientation={false}
                guides={false}
                scalable={false}
                cropBoxMovable
                cropBoxResizable
                background={false}
                responsive
                ref={cropperRef}
                dragMode="crop"
              />
            </Box>
          )}

          {error && (
            <Typography
              color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
              mt={3}
            >
              {error}
            </Typography>
          )}

          <Box sx={buttonContainerSx}>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              hierarchy="Primary"
              size="lg"
            >
              {imageSrc ? IMAGE_UPLOAD_STRINGS.changeImage : IMAGE_UPLOAD_STRINGS.selectImage}
            </Button>
            {imageSrc && (
              <Box sx={cancelUploadButtonGroupSx}>
                <Button onClick={handleCancel} hierarchy="Secondary gray" size="lg" fullWidth>
                  {BUTTON_STRINGS.cancel}
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!imageSrc || isUploading}
                  hierarchy="Primary"
                  size="lg"
                  fullWidth
                >
                  {isUploading ? IMAGE_UPLOAD_STRINGS.uploading : IMAGE_UPLOAD_STRINGS.upload}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      }
      onClose={handleCancel}
      open={open}
      width="sm"
    />
  );
};

const headerContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const bodyContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const cropperWrapperSx =
  (isProfileImage: boolean): SxProps<Theme> =>
  () => ({
    width: '100%',
    position: 'relative',
    mx: 'auto',
    maxWidth: '600px',
    overflow: 'hidden',
    height: isProfileImage ? 276 : 250,
    '& .cropper-container .cropper-view-box, & .cropper-container .cropper-face': {
      borderRadius: isProfileImage ? '50% !important' : '0 !important',
      overflow: 'hidden !important',
    },
    '& .cropper-container .cropper-view-box': {
      boxShadow: isProfileImage ? '0 0 0 9999px rgba(0,0,0,0.5) !important' : 'none !important',
    },
  });

const cropperStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  maxWidth: '100%',
};

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  justifyContent: 'space-between',
  marginTop: backrs.spacing['spacing-2xl'],
  flexWrap: 'wrap',
});

const cancelUploadButtonGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignSelf: 'flex-end',
});
